import { useEffect, type ReactNode } from 'react';

import type { NotificationItem } from '../api/types.ts';
import { formatRsd, para } from '../core/money.ts';
import { OrderStatusGroup } from '../core/order-status.ts';
import { useStore } from '../core/store.ts';
import { orderMoment, relativeTime, statusText, useLang, useStrings, type Lang, type Strings } from '../i18n/index.ts';
import { lookupProduct } from '../state/reorder.ts';
import { pushRoute } from '../state/router.ts';
import { markAllNotificationsRead, notificationsStore, refreshNotifications } from '../state/stores.ts';
import { notify } from '../state/toasts.ts';
import { Icon } from '../ui/icons.tsx';
import { EmptyState, pastel, ScreenTitle } from '../ui/kit.tsx';
import { Screen } from '../ui/Screen.tsx';
import { Glyph, orderRowIcon } from './OrderParts.tsx';

import '../styles/orders.css';

/**
 * Уведомления: движение заказов, промо, ответы поддержки, бонусы и повторные доставки одним
 * списком. Открытие экрана отмечает всё прочитанным; тап по строке кладёт заказ, переписку
 * или товар на тот же стек.
 */
export function NotificationsScreen() {
  const strings = useStrings();
  const { items, isLoading } = useStore(notificationsStore);

  useEffect(() => {
    void refreshNotifications().then(markAllNotificationsRead);
  }, []);

  return (
    <Screen>
      <div className="readable">
        <ScreenTitle text={strings.notificationsTitle} />
        {items.length === 0 && !isLoading ? (
          <div className="notifications-empty">
            <EmptyState image="/empty/notifications.webp" title={strings.notificationsEmpty} hint={strings.notificationsEmptyHint} />
          </div>
        ) : (
          <div className="gutter stack notifications-list">
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}

/** Куда ведёт строка; null — строка не нажимается. */
function actionOf(item: NotificationItem, strings: Strings): (() => void) | null {
  if (item.kind === 'support') return () => pushRoute({ name: 'support', orderId: item.orderId });
  const { orderId, productId } = item;
  if (orderId) return () => pushRoute({ name: 'order', orderId });
  if (productId) {
    return () => {
      void lookupProduct(productId).then((found) => {
        if (found) pushRoute({ name: 'product', item: found });
        else notify(item.title ? `${item.title}: ${strings.itemGone}` : strings.itemGone);
      });
    };
  }
  return null;
}

function titleOf(item: NotificationItem, strings: Strings): string {
  switch (item.kind) {
    case 'promo':
      return item.title ?? '';
    case 'support':
      return strings.notificationSupportReply;
    case 'credit':
      return strings.notificationCredit(item.amountRsd ? formatRsd(item.amountRsd) : '');
    case 'redelivery':
      return strings.notificationRedelivery;
    case 'zone_open':
      return strings.notificationZoneOpen;
    case 'delivery_code':
      return strings.notificationDeliveryCode;
    case 'short_pick':
      return strings.notificationShortPick;
    case 'return_refund':
      return strings.notificationReturnRefund;
    default:
      return statusText(strings, item.orderStatus ?? '');
  }
}

function bodyOf(item: NotificationItem, strings: Strings, lang: Lang): string {
  switch (item.kind) {
    case 'credit':
      // Системные причины бонуса приходят кодом, причина оператора — готовым текстом.
      if (item.body === 'order_cancelled') return strings.creditReasonOrderCancelled;
      if (item.body === 'order_adjusted') return strings.creditReasonOrderAdjusted;
      return item.body ?? '';
    case 'promo':
    case 'support':
      return item.body ?? '';
    case 'redelivery':
      return strings.notificationRedeliveryHint;
    case 'zone_open':
      return item.addressText ?? strings.notificationZoneOpenHint;
    case 'delivery_code':
      return strings.notificationDeliveryCodeHint;
    case 'short_pick':
    case 'return_refund': {
      const missing = item.body ?? '';
      if (para(item.amountRsd) <= 0n) return missing;
      return [missing, strings.notificationShortPickRefund(formatRsd(item.amountRsd))].filter(Boolean).join(' · ');
    }
    default:
      return orderMoment(lang, item.createdAt);
  }
}

/** Иконка и цвет строки заказа те же, что у строки в списке заказов. */
function iconOf(item: NotificationItem): ReactNode {
  switch (item.kind) {
    case 'promo':
      return <Icon name="tag" size={18} />;
    case 'support':
      return <Icon name="chat" size={18} />;
    case 'credit':
      return <Icon name="gift" size={18} />;
    case 'redelivery':
      return <Glyph name="redelivery" size={18} weight={2.2} />;
    case 'zone_open':
      return <Icon name="pin" size={18} />;
    case 'delivery_code':
      return <Icon name="bell" size={18} />;
    case 'short_pick':
    case 'return_refund':
      return <Glyph name="undo" size={18} weight={2.4} />;
    default:
      return <Icon name={orderRowIcon(item.orderStatus ?? '')} size={18} weight={2.4} />;
  }
}

function NotificationRow({ item }: { item: NotificationItem }) {
  const strings = useStrings();
  const lang = useLang();
  const warm = item.kind === 'promo' || item.kind === 'credit';
  const tint = warm ? 'var(--sale)' : OrderStatusGroup.cancelled.has(item.orderStatus ?? '') ? 'var(--danger)' : 'var(--accent-ink)';
  const action = actionOf(item, strings);

  return (
    <button type="button" className="notification-row pressable" disabled={!action} onClick={action ?? undefined}>
      <span className="notification-icon" style={{ color: tint, background: warm ? pastel(1) : 'var(--accent-soft)' }}>
        {iconOf(item)}
      </span>
      {/* Главное в строке заказа — что произошло; время идёт вторым. */}
      <span className="grow stack notification-text">
        <b>{titleOf(item, strings)}</b>
        <span className="t-muted">{bodyOf(item, strings, lang)}</span>
        <span className="t-caption t-muted notification-time">{relativeTime(lang, item.createdAt)}</span>
      </span>
      {!item.readAt && <span className="notification-unread" />}
    </button>
  );
}
